import { TableCell, TableRow } from "@mui/material";
import {
  CensusNotUsedNote,
  CensusUploadCheck,
} from "@platform/census-validation";
import { ErrorBoundary, Typography } from "@platform/design-system";

import { useRfpContext } from "../../contexts";

interface CensusFileCheckRowProps {
  readonly documentId: number;

  /** The RFP is quoted on its newest census file only. */
  readonly isNewest: boolean;

  /** Uploaded since the step opened, so its suggestions open without a click. */
  readonly isJustUploaded: boolean;
}

const CheckUnavailable = (): React.JSX.Element => (
  <Typography variant="caption" color="textSecondary">
    {"We couldn't check this file right now."}
  </Typography>
);

const NewestCensusCheck = ({
  documentId,
  isJustUploaded,
}: Omit<CensusFileCheckRowProps, "isNewest">): React.JSX.Element => {
  const { rfp } = useRfpContext();
  const rfpId = rfp.id;

  if (typeof rfpId !== "number") {
    throw new TypeError("A census file was uploaded to an RFP with no id");
  }

  return (
    <CensusUploadCheck
      rfpId={rfpId}
      documentId={documentId}
      openSuggestions={isJustUploaded}
    />
  );
};

/**
 * The census check for one uploaded census file, under that file's row. A
 * failure stays inside the row, so the rest of the step keeps working.
 */
export const CensusFileCheckRow = ({
  documentId,
  isNewest,
  isJustUploaded,
}: CensusFileCheckRowProps): React.JSX.Element => (
  <TableRow data-testid="census-file-check-row">
    <TableCell colSpan={4} sx={{ paddingTop: 0 }}>
      <ErrorBoundary fallback={CheckUnavailable}>
        {isNewest ? (
          <NewestCensusCheck
            documentId={documentId}
            isJustUploaded={isJustUploaded}
          />
        ) : (
          <CensusNotUsedNote />
        )}
      </ErrorBoundary>
    </TableCell>
  </TableRow>
);
